import Api from '@teko/api';
import { accessTokenInterceptor } from '@teko/auth';
import { SORT_ORDER, SORT_TYPE } from './constants.es.js';

const cleanObject = (object) => {
    const newObj = {};
    Object.keys(object).forEach((key) => {
        if (object[key] || object[key] === false || object[key] === 0) {
            newObj[key] = object[key];
        }
    });
    return newObj;
};
const normalizePageBuilderToDiscoveryParams = (params) => {
    var _a, _b, _c, _d;
    if (!params)
        return {};
    const attributes = params['attributes'] || [];
    const priceGteFromPageBuilder = (_b = (_a = params === null || params === void 0 ? void 0 : params.prices) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.from;
    const priceLteFromPageBuilder = (_d = (_c = params === null || params === void 0 ? void 0 : params.prices) === null || _c === void 0 ? void 0 : _c[0]) === null || _d === void 0 ? void 0 : _d.to;
    if (!params['price_gte'] && priceGteFromPageBuilder)
        params['price_gte'] = priceGteFromPageBuilder;
    if (!params['price_lte'] && priceLteFromPageBuilder)
        params['price_lte'] = priceLteFromPageBuilder;
    attributes.forEach((element) => {
        const key = `attributes.${element.code}`;
        const value = element.optionIds && element.optionIds.toString();
        params[key] = value;
    });
    params['attributes'] = [];
    return cleanObject(params);
};
const normalizeDiscoveryApiParams = (params) => {
    const listingAndDiscoveryFieldsMap = {
        _sort: 'sort',
        _order: 'order',
        bestPrice_gte: 'priceGte',
        bestPrice_lte: 'priceLte',
        price_lte: 'priceLte',
        price_gte: 'priceGte',
        _limit: 'itemsPerPage',
        limit: 'itemsPerPage',
        sellerCategories: 'masterCategoryIds',
        _page: 'pageNumber',
        page: 'pageNumber',
        q: 'query',
        keyword: 'query',
        skus_ne: 'excludedSkus',
        providerId: 'providerIds',
    };
    const normalizedParams = {};
    Object.keys(params).forEach((field) => {
        if (listingAndDiscoveryFieldsMap[field]) {
            normalizedParams[listingAndDiscoveryFieldsMap[field]] = params[field];
        }
        else {
            normalizedParams[field] = params[field];
        }
    });
    return normalizedParams;
};
const convertArrayValue = (value, returnType = 'string') => {
    if (typeof value === 'string') {
        if (returnType === 'string')
            return value.split(',');
        return value.split(',').map((el) => +el);
    }
    if (returnType === 'string')
        return value;
    return value.map((el) => +el);
};
class DiscoveryService {
    constructor(discoveryApiUrl, ecomApiUrl = '/api', language) {
        this._isArrayValue = [
            'sellerCodes',
            'brands',
            'categories',
            'tags',
            'providerIds',
            'sellerIds',
            'skus',
            'masterCategories',
            'masterCategoryIds',
            'excludedSkus',
            'returnFilterable',
            'excludedSkus',
            'clearanceTypeIds',
        ];
        this._discoveryPayloadKeys = [
            'terminalCode',
            'query',
            'filter',
            'pagination',
            'sorting',
            'userId',
            'phoneNumber',
            'location',
            'returnFilterable',
            'block',
            'pos365StoreCode',
        ];
        this._discoveryBlockKeys = ['blockId', 'itemId'];
        this._discoveryFilterKeys = [
            'sellingCodes',
            'priceLte',
            'priceGte',
            'brands',
            'categories',
            'tags',
            'hasPromotions',
            'attributes',
            'providerIds',
            'sellerIds',
            'skus',
            'flashSaleType',
            'masterCategories',
            'masterCategoryIds',
            'excludedSkus',
            'clearanceTypeIds',
        ];
        this._isBooleanValue = ['hasPromotions'];
        this._isNumberValue = ['clearanceTypeIds'];
        this._discoverySortingKeys = ['sort', 'order'];
        this._discoveryPaginationKeys = ['pageNumber', 'itemsPerPage'];
        this._orderValuesMap = {
            desc: SORT_ORDER.ORDER_BY_DESCENDING,
            asc: SORT_ORDER.ORDER_BY_ASCENDING,
            DESC: SORT_ORDER.ORDER_BY_DESCENDING,
            ASC: SORT_ORDER.ORDER_BY_ASCENDING,
        };
        this._sortValuesMap = {
            new: 'SORT_BY_PUBLISH_AT',
            bestPrice: 'SORT_BY_PRICE',
            'view.last_3_day': SORT_TYPE.SORT_BY_MOST_VIEW,
            'view.last_7_day': SORT_TYPE.SORT_BY_MOST_VIEW_7_DAYS,
            'view.last_30_day': SORT_TYPE.SORT_BY_MOST_VIEW_30_DAYS,
            'view.last_90_day': SORT_TYPE.SORT_BY_MOST_VIEW_90_DAYS,
            'quantity.last_3_day': SORT_TYPE.SORT_BY_TOP_SALE_QUANTITY,
            'quantity.last_1_week': SORT_TYPE.SORT_BY_TOP_SALE_QUANTITY_7_DAYS,
            'quantity.last_1_month': SORT_TYPE.SORT_BY_TOP_SALE_QUANTITY_30_DAYS,
            'quantity.last_3_month': SORT_TYPE.SORT_BY_TOP_SALE_QUANTITY_90_DAYS,
            discountPercent: SORT_TYPE.SORT_BY_DISCOUNT_PERCENT,
            discount: SORT_TYPE.SORT_BY_DISCOUNT_PERCENT,
        };
        this.discoveryApi = new Api(discoveryApiUrl);
        this.discoveryApi.addInterceptor(accessTokenInterceptor);
        this.discoveryApi.addInterceptor((request) => {
            request.headers.set('Accept-Language', language || '*');
        });
        this.serverApi = new Api(ecomApiUrl);
    }
    _cookPayload(params) {
        const payload = {
            filter: {},
            pagination: {},
            query: '',
            sorting: {},
            returnFilterable: [],
            terminalCode: '',
            block: {},
        };
        const normalizedParams = normalizeDiscoveryApiParams(normalizePageBuilderToDiscoveryParams(params));
        const attributes = [];
        Object.keys(normalizedParams).forEach((key) => {
            let data = this._isArrayValue.includes(key)
                ? convertArrayValue(normalizedParams[key], this._isNumberValue.includes(key) ? 'number' : 'string')
                : normalizedParams[key];
            if (key.startsWith('attributes.')) {
                attributes.push({
                    code: key.replace('attributes.', ''),
                    optionIds: data.split(','),
                });
            }
            if (this._isBooleanValue.includes(key)) {
                data = data === 'true' || data === true;
            }
            if (this._discoveryPayloadKeys.includes(key)) {
                payload[key] = data;
            }
            if (this._discoveryBlockKeys.includes(key)) {
                payload.block[key] = data;
            }
            if (this._discoveryFilterKeys.includes(key)) {
                payload.filter = Object.assign(Object.assign({}, payload.filter), { [key]: data });
            }
            if (this._discoverySortingKeys.includes(key)) {
                payload.sorting = Object.assign(Object.assign({}, payload.sorting), {
                    [key]: key === 'order'
                        ? this._orderValuesMap[data] || data || 'ORDER_BY_UNSPECIFIED'
                        : this._sortValuesMap[data] || data || 'SORT_BY_UNSPECIFIED',
                });
            }
            if (this._discoveryPaginationKeys.includes(key)) {
                payload.pagination = Object.assign(Object.assign({}, payload.pagination), {
                    [key]: data,
                });
            }
        });
        payload['filter']['attributes'] = [
            ...(payload['filter']['attributes'] || []),
            ...attributes,
        ];
        return payload;
    }
    getBaseUrl() {
        return this.discoveryApi.baseURL;
    }
    async search(params) {
        const searchParams = this._cookPayload(params);
        const { data } = await this.discoveryApi.post('/search', searchParams);
        return data;
    }
    async internalSearch(terminalCode, siteId, query, limit, sorting) {
        var _a;
        const { data } = await this.discoveryApi.post('/internal/search', {
            terminalCode,
            siteId,
            query,
            sorting,
            limit,
        });
        if ((data === null || data === void 0 ? void 0 : data.code) !== '0' || !((_a = data === null || data === void 0 ? void 0 : data.data) === null || _a === void 0 ? void 0 : _a.products)) {
            return [];
        }
        return data.data.products;
    }
    async getProductDetailBySkus(params) {
        var _a;
        const { data } = await this.discoveryApi.get('/products', {
            params,
        });
        return ((_a = data === null || data === void 0 ? void 0 : data.result) === null || _a === void 0 ? void 0 : _a.products) || [];
    }
    async getProduct(params, callFromDiscovery = true) {
        const { sku, terminalCode, location } = params;
        let apiParams = null;
        let api = this.serverApi;
        if (callFromDiscovery) {
            api = this.discoveryApi;
            apiParams = {
                terminalCode,
                location,
            };
        }
        const { data } = await api.get(`/product/${sku}`, {
            params: apiParams,
        });
        return data;
    }
    async getKeyword(params) {
        const { data } = await this.discoveryApi.get('/keywords', {
            params,
        });
        return data;
    }
    async getBlock(params) {
        const { data } = await this.discoveryApi.get(`/blocks`, {
            params,
        });
        return data;
    }
    async getPopularKeyword(params) {
        const { data } = await this.discoveryApi.get(`/recommended-search-terms`, {
            params,
        });
        return data;
    }
    async getRecommendationProducts(sku, terminalCode) {
        const { data } = await this.discoveryApi.get(`/products/${sku}/recommendations`, {
            params: { terminalCode },
        });
        return data;
    }
    async getRecommendationCombos(sku, params) {
        const { data } = await this.discoveryApi.get(`/products/${sku}/combos`, {
            params,
        });
        return data;
    }
    async getDetailComboRecommendation(params) {
        const { data } = await this.discoveryApi.post(`/combos`, params);
        return data;
    }
    async getMenuItems(terminal, zone) {
        const result = await this.discoveryApi.get(`/menu-items`, {
            params: {
                terminal,
                zone: zone,
                _limit: 1000,
                _sort: 'priority',
                _order: 'asc',
            },
        });
        return result.data;
    }
    async getMenuItemsZonePrefix(terminal, zonePrefix) {
        const result = await this.discoveryApi.get(`/menu-items`, {
            params: {
                terminal,
                zonePrefix,
                _limit: 10000,
                _sort: 'priority',
                _order: 'asc',
            },
        });
        return result.data;
    }
    async getMenuItemById(categoryId) {
        const { data } = await this.discoveryApi.get(`/menu-item/${categoryId}`);
        return data;
    }
    async searchProductsByBarcode(reqParams) {
        const { sku, barcode, sellerId, terminalCode } = reqParams;
        const params = {
            identifiers: [
                {
                    sellerSkuIdentifier: {
                        sellerId,
                        sellerSku: sku,
                    },
                },
                {
                    sellerBarcodeIdentifier: {
                        barcode,
                        sellerId,
                    },
                },
            ],
            terminalCode,
        };
        const { data } = await this.discoveryApi.post(`/products`, params);
        return data;
    }
}

export { DiscoveryService, cleanObject, normalizePageBuilderToDiscoveryParams };
